import React, { createContext, FC, useContext, useMemo } from 'react';
import { getBackendBaseUrl } from 'src/api/apiUtils';
import { joinChatApi } from 'src/api/joinChatAPI';
import { joinSession } from 'src/api/joinSessionAPI';
import { startVideoApi } from 'src/api/startVideoAPI';
import { leaveSessionApi } from 'src/api/leaveSessionAPI';
import { activeParticipantsApi } from 'src/api/activeParticipantsAPI';
import { appointmentApi } from 'src/api/appointmentAPI';
import { deeplinkApi } from 'src/api/deeplinkAPI';
import { brandNameApi } from 'src/api/brandNameAPI';
import { medicalGroupApi } from 'src/api/medicalGroupAPI';
import { oneWayMessagesApi } from 'src/api/oneWayMessageAPI';
import { refreshedJWTApi } from 'src/api/refreshJWTAPI';
import { refreshChatTokenApi } from 'src/api/refreshChatTokenAPI';
import { ambientRecordingStateApi, type AmbientRecordingStateResponse } from 'src/api/ambientRecordingStateAPI';
import { useChimeRegionState } from 'src/components/provider/ChimeRegionProvider';

interface BackendAPIState {
  ambientRecordingStateApi: (
    jwt: string,
    meetingNumber: string,
    signal?: AbortSignal
  ) => Promise<AmbientRecordingStateResponse>;
  activeParticipantsApi: (jwt: string) => Promise<ActiveParticipantsResponse>;
  appointmentApi: (token: string) => Promise<Response>;
  baseUrl: string;
  brandNameApi: (token: string) => Promise<string>;
  medicalGroupApi: (token: string) => Promise<MedicalGroupResponse>;
  deeplinkApi: (code: string) => Promise<Response>;
  joinChatApi: (jwt: string, retryCount?: number) => Promise<JoinChatSuccessResponse>;
  joinSessionApi: (token: string, displayName: string, inCallChat?: boolean) => Promise<JoinSessionSuccessResponse>;
  leaveSessionApi: (jwt: string) => Promise<LeaveSessionResponse>;
  oneWayMessageApi: (jwt: string) => Promise<OneWayMessageResponse>;
  refreshJWTApi: (jwt: string) => Promise<RefreshJWTResponse>;
  refreshChatTokenApi: (jwt: string, userArn: string) => Promise<JoinChatSuccessResponse>;
  startVideoApi: (jwt: string, readinessCheck?: boolean) => Promise<StartVideoSuccessResponse>;
}

export const BackendContext = createContext<BackendAPIState | null>(null);

const setBaseUrl = (region: string): BackendAPIState => {
  const baseUrl = getBackendBaseUrl();
  return {
    ambientRecordingStateApi: (jwt: string, meetingNumber: string, signal?: AbortSignal) =>
      ambientRecordingStateApi(jwt, meetingNumber, signal),
    activeParticipantsApi: (jwt: string) => activeParticipantsApi(jwt),
    appointmentApi: (token: string) => appointmentApi(token),
    baseUrl,
    brandNameApi: (token: string) => brandNameApi(token),
    medicalGroupApi: (token: string) => medicalGroupApi(token),
    deeplinkApi: (code: string) => deeplinkApi(code),
    joinChatApi: (jwt: string, retryCount = 4) => joinChatApi(jwt, retryCount),
    joinSessionApi: (token: string, displayName: string, inCallChat?: boolean) =>
      joinSession({ token, displayName, inCallChat }),
    leaveSessionApi: (jwt: string) => leaveSessionApi(jwt),
    startVideoApi: (jwt: string, readinessCheck?: boolean) => startVideoApi(jwt, region, readinessCheck),
    oneWayMessageApi: (jwt: string) => oneWayMessagesApi(jwt),
    refreshJWTApi: (jwt: string) => refreshedJWTApi(jwt),
    refreshChatTokenApi: (jwt: string, userArn: string) => refreshChatTokenApi(jwt, userArn),
  };
};

const BackendProvider: FC<WithChildren> = ({ children }) => {
  const { region } = useChimeRegionState();
  const value = useMemo(() => setBaseUrl(region), [region]);

  return <BackendContext.Provider value={value}>{children}</BackendContext.Provider>;
};

export const useBackend = () => {
  const state = useContext(BackendContext);
  if (!state) {
    throw new Error('useBackend must be used within a BackendProvider');
  }

  return state;
};

export default BackendProvider;
